import { Program } from "../../../../models/program";

/**
 * ── LEGACY SHELL — SCHEDULED FOR DELETION ──────────────────────────────────
 *
 * The original did not have one chrome — each screen passed its own options to
 * dashboardHeader and decided for itself whether to sit on the tinted field.
 * The refactored shell applies one chrome everywhere, so reproducing the old
 * app means reproducing the per-screen differences. This table is that.
 *
 * Taken from what each screen actually passed on `frontend_refactor`:
 *   analytics.tsx    headerType="analytic", lastUpdated set  -> all lines + filters
 *   equipments.tsx   headerType="equipment", NO lastUpdated  -> no Last Update, no filters
 *   trainingView     showCurrency/LastUpdated/StartingDate={false}, and the
 *                    header lived INSIDE the user_grades branch -> name only,
 *                    nothing at all on User Management
 *   salesRepView     headerType="" -> all lines, no filters, no tinted wrapper
 *   systemManagement NO dashboardHeader at all -> tab strip straight onto the
 *                    tinted field, no title block
 */
export interface LegacyScreenChrome {
  /** The pale field with 10px white rails. Only the dashboard views had it. */
  tint: boolean;
  /** Render the title block at all. */
  title: boolean;
  startingDate: boolean;
  lastUpdate: boolean;
  currency: boolean;
  /** The Branches / Departments pair. */
  filters: boolean;
  /**
   * Replaces the bar's own `10px 20px` where the screen's wrapper changed it.
   *
   * The original's title block was never a sibling of the content: it sat
   * INSIDE the screen's wrapper and inherited that wrapper's padding. Pinning
   * moved it out, so each screen states what its box should now be.
   *
   * Note `bottom` is a SEPARATE number, and for training it is 0. The wrapper
   * is still in the screen's own JSX below the bar, so its `padding-top` — which
   * in the original sat ABOVE the header — now falls between the bar and the
   * content. Keeping the header's own 10 as well counts that gap twice, which
   * is exactly the space that appeared under the client title.
   *
   *   tinted screens   default 10/10/20   the bar draws the white rails instead
   *   training grades  20 / 0 / 40        wrapper 10px 20px + header 10px 20px,
   *                                       minus the 10 the wrapper still adds
   *   sales rep        default            dashboardHeader was a direct child
   */
  barPadding?: { top: number; bottom: number; x: number };
  /**
   * Show the bar's seam at rest, not only once <main> scrolls.
   *
   * Screens with a tab strip get their separation from it. The sales-rep view
   * has no strip, so its title block ran straight into the Branch / Sale Rep
   * selects with nothing between them.
   */
  divider?: boolean;
  /**
   * Top padding for <main>, for untinted screens whose content would otherwise
   * start flush against the bar. Tinted screens get their spacing from the
   * field background instead.
   */
  mainPadTop?: number;
}

const ANALYTICS: LegacyScreenChrome = {
  tint: true,
  title: true,
  startingDate: true,
  lastUpdate: true,
  currency: true,
  filters: true
};


// trainingView passed all three switches false, so only the client name showed.
// Its dashboardHeader sat inside a `padding: "10px 20px"` grid that also held
// the grades table. Measured on frontend_refactor's box model, the title has
// 20px of air above it (wrapper 10 + header 10), 20px between it and the green
// panel (header 10 + the panel's own grid 10), and sits at x=40 (20 + 20).
//
// bottom is 0 because that wrapper is still in trainingView's JSX below the bar
// and contributes its own 10 there — see barPadding.
const TRAINING_GRADES: LegacyScreenChrome = {
  tint: false,
  title: true,
  startingDate: false,
  lastUpdate: false,
  currency: false,
  filters: false,
  barPadding: { top: 20, bottom: 0, x: 40 }
};

// The header lived inside the user_grades branch, so this tab had none. The tab
// also brings its own tinted wrapper, which is why tint stays false here — the
// shell would otherwise double the rails and darken the field.
const TRAINING_USERS: LegacyScreenChrome = { ...TRAINING_GRADES, title: false };

// Sales reps ran under the Analytics program but rendered no tinted wrapper and
// no filters — the view has its own Branch and Sales Rep selects.
// No tab strip on this screen, so the title block needs its own seam and the
// content below it needs room — see `divider` and `mainPadTop`.
const SALES_REP: LegacyScreenChrome = {
  ...ANALYTICS,
  tint: false,
  filters: false,
  barPadding: { top: 10, bottom: 14, x: 20 },
  divider: true,
  mainPadTop: 16
};

// System Management had NO title block. `systemManagement.tsx` on
// frontend_refactor opens straight onto the grey tab strip and never imports
// dashboardHeader, so there was nothing to show a client name, starting date or
// currency — and those fields are empty on that screen anyway. It did keep the
// tinted field with the 10px white rails.
const SYSTEM: LegacyScreenChrome = {
  tint: true,
  title: false,
  startingDate: false,
  lastUpdate: false,
  currency: false,
  filters: false
};

export const legacyChromeFor = (
  programIndex: number | undefined,
  selectedItemId: string,
  archetype: string,
  system = false
): LegacyScreenChrome => {
  if (system) return SYSTEM;
  if (archetype === "worklist") return SALES_REP;
  // Archetype BEFORE programIndex, and this is not belt-and-braces: on the
  // learning-user route `account.tsx` renders TrainingView directly and never
  // dispatches setSelectedProgram — true on frontend_refactor too — so
  // programIndex is undefined and the Training check below misses. The screen
  // then fell through to ANALYTICS and printed "Starting Date:" and
  // "Currency :" with empty values, where the original showed the client name
  // alone. useNavConfig already infers the program when the store has not set
  // it, and reports "roster" for Training; trust that, as the worklist case
  // above already does. The programIndex check still serves the multi-account
  // route, where the chooser does set it.
  if (archetype === "roster") {
    return selectedItemId === "users" ? TRAINING_USERS : TRAINING_GRADES;
  }
  if (programIndex === Program.Index.Training) {
    return selectedItemId === "users" ? TRAINING_USERS : TRAINING_GRADES;
  }
  return ANALYTICS;
};

/**
 * The refresh renamed these to sentence case. The legacy strip printed the old
 * labels, so it maps them back rather than the shared nav data carrying two
 * spellings for every theme.
 */
export const LEGACY_NAV_LABELS: Record<string, string> = {
  growthForecastRetention: "Growth/Forecast & Retention",
  seasonality: "Store & Seasonality",
  user_grades: "User Grades",
  users: "User Management"
};
